/**
 * @fileoverview ข้อมูลบริษัทที่ใช้แสดงผลทั้งเว็บ (โลโก้เป็นหลัก)
 * @module services/company
 *
 * ที่มาของข้อมูล : GET company (เปิดสาธารณะ) — ตารางเดียวกับหน้า "ตั้งค่า › ข้อมูลบริษัท"
 *
 * ทำไมต้องมีตัวกลางตัวนี้ :
 *   โลโก้ถูกใช้หลายที่ (เมนูแอดมิน หน้า login เมนู/ฟุตเตอร์เว็บลูกค้า และ favicon)
 *   ถ้าให้แต่ละหน้ายิง API เอง จะยิงซ้ำและอัปเดตไม่พร้อมกัน จึงโหลดครั้งเดียวตอนเปิดเว็บ
 *   แล้วให้ทุกจุด subscribe ค่าเดียวกัน — บันทึกโลโก้ใหม่ที่หน้าตั้งค่าแล้วเปลี่ยนทันที
 *
 * เก็บ cache ลง localStorage ด้วย เพื่อให้รอบถัดไปโลโก้ขึ้นตั้งแต่เฟรมแรก
 * ไม่ต้องเห็นโลโก้ตั้งต้นแวบหนึ่งก่อนแล้วค่อยสลับ
 */

const STORAGE_KEY = "mn-company";

/** ค่าตั้งต้น — ไม่มีโลโก้ = ทุกจุดใช้โลโก้ตั้งต้นของระบบ (SVG ใน components/Logo.js) */
export const EMPTY_COMPANY = {
  logo: "",
  name_th: "",
  name_en: "",
  license_no: "",
  phone: "",
  hotline: "",
  email: "",
  website: "",
  line_oa: "",
  facebook: "",
  address: "",
};

let company = readCache();
let request = null;
const listeners = new Set();

function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return cached ? { ...EMPTY_COMPANY, ...cached } : { ...EMPTY_COMPANY };
  } catch (e) {
    return { ...EMPTY_COMPANY };
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {}
}

/**
 * ค่าที่ถืออยู่ตอนนี้ (อ่านได้ทันที ไม่ต้องรอ API)
 * @returns {Object}
 */
export const getCompany = () => company;

/**
 * โลโก้ปัจจุบัน — string ว่าง = ยังไม่ได้ตั้ง ให้ใช้โลโก้ตั้งต้น
 * @returns {string}
 */
export const getCompanyLogo = () => company.logo || "";

/**
 * รับแจ้งเมื่อข้อมูลบริษัทเปลี่ยน (โหลดเสร็จ / เพิ่งบันทึกที่หน้าตั้งค่า)
 * @param {Function} listener
 * @returns {Function} ฟังก์ชันยกเลิกการรับแจ้ง
 */
export const subscribeCompany = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * ตั้งค่าข้อมูลบริษัทชุดใหม่แล้วแจ้งทุกจุดที่ใช้อยู่
 * หน้าตั้งค่าเรียกอันนี้หลังบันทึกสำเร็จ เพื่อให้โลโก้บนเมนูและ favicon เปลี่ยนทันที
 * โดยไม่ต้องรีโหลดหน้า
 * @param {Object} data
 */
export const setCompany = (data) => {
  // หยิบเฉพาะคีย์ที่ประกาศไว้ เพราะฝั่งแอดมินส่งข้อมูลชุดเต็ม (มี tax_id ด้วย)
  // มาให้ตอนบันทึก — ข้อมูลที่ไม่ได้ใช้แสดงผลไม่ควรตกไปอยู่ใน cache ที่ทุกหน้าอ่านได้
  const source = data || {};
  company = Object.keys(EMPTY_COMPANY).reduce(
    (acc, key) => ({ ...acc, [key]: source[key] ?? EMPTY_COMPANY[key] }),
    {}
  );
  writeCache(company);
  applyFavicon(company.logo);
  listeners.forEach((listener) => listener(company));
};

/**
 * เปลี่ยน favicon ของแท็บให้เป็นโลโก้บริษัท
 * ไม่มีโลโก้ = คืนค่าเป็นไอคอนตั้งต้นที่ประกาศไว้ใน index.html
 * @param {string} logo - URL โลโก้ (หรือ base64 ตอนพรีวิว)
 */
export const applyFavicon = (logo) => {
  if (typeof document === "undefined") return;

  [
    { rel: "icon", selector: "link[rel~='icon']" },
    { rel: "apple-touch-icon", selector: "link[rel='apple-touch-icon']" },
  ].forEach(({ rel, selector }) => {
    /* index.html ประกาศไอคอนไว้หลายใบต่อหนึ่ง rel (favicon.ico คู่กับ logo.svg)
       ถ้าเปลี่ยนแค่ใบแรก เบราว์เซอร์อาจไปหยิบใบที่เหลือมาแสดงแทน โลโก้บริษัทจึงไม่ขึ้น */
    const links = Array.from(document.querySelectorAll(selector));
    if (!links.length) {
      if (!logo) return;
      const created = document.createElement("link");
      created.rel = rel;
      document.head.appendChild(created);
      links.push(created);
    }

    links.forEach((link) => {
      /* ค่าตั้งต้นของแต่ละใบ เก็บไว้ที่ตัว element ครั้งแรกที่แตะ ก่อนจะถูกทับด้วยโลโก้
         เพื่อให้ย้อนกลับได้เมื่อผู้ดูแลลบโลโก้ออก */
      if (link.dataset.defaultHref === undefined) link.dataset.defaultHref = link.getAttribute("href") || "";

      const href = logo || link.dataset.defaultHref;
      if (!href) return;
      if (link.getAttribute("href") === href) return;

      link.setAttribute("href", href);
      // ไม่ระบุ type ปล่อยให้เบราว์เซอร์เดาเอง เพราะโลโก้อาจเป็น png/jpg/svg
      link.removeAttribute("type");
    });
  });
};

/**
 * โหลดข้อมูลบริษัทจาก API (ครั้งเดียวต่อการเปิดเว็บ)
 *
 * ยิง fetch ตรงแทนที่จะใช้ GET ของ services/api เพราะอันนั้นเด้ง popup เตือน
 * เมื่อ API ล่ม — งานนี้เป็นงานเบื้องหลังของทุกหน้า ล้มเหลวแล้วควรเงียบ
 * แล้วใช้โลโก้ตั้งต้นไปก่อน ไม่ใช่ขึ้นเตือนคาหน้า login
 *
 * @param {boolean} force - true = ดึงใหม่แม้เคยดึงไปแล้ว
 * @returns {Promise<Object>}
 */
export const loadCompany = (force = false) => {
  if (request && !force) return request;

  request = fetch(`${process.env.REACT_APP_API_URL}company`, {
    method: "GET",
    headers: { accept: "application/json" },
  })
    .then((response) => response.json())
    .then((result) => {
      if (result?.status === true && result.data) setCompany(result.data);
      return company;
    })
    .catch(() => company);

  return request;
};

const companyService = {
  EMPTY_COMPANY,
  getCompany,
  getCompanyLogo,
  subscribeCompany,
  setCompany,
  applyFavicon,
  loadCompany,
};

export default companyService;
